import { Appearance } from '../../components/Appearance';
import { businessInitials } from '@shared/business-identity';
import { LanguageSwitcher } from './LanguageSwitcher';
import { LegalFooter } from '../../components/LegalFooter';
import { useI18n } from '../lib/i18n';
import type { ReactNode } from 'react';
import type { PublicPage } from '../types';
import { ensureHttp, hostOf } from '../lib/format';
import { GlobeIcon, MailIcon, PhoneIcon, PinIcon, SocialIcon } from './Icons';
import { hasPaymentInfo, PaymentMethods, type PaymentFields } from './Payment';

type ContactFields = Pick<PublicPage, 'contact_email' | 'contact_phone' | 'website_url' | 'address'> &
  Partial<Pick<PublicPage, 'social_links'>>;

/** Whether the business has published any way to reach it. */
export function hasContact(page: Partial<ContactFields>): boolean {
  const socials = Object.values(page.social_links ?? {}).filter((url) => typeof url === 'string' && url);
  return Boolean(page.contact_phone || page.contact_email || page.website_url || page.address || socials.length > 0);
}

export function ContactList({ page }: { page: Partial<ContactFields> }) {
  const { t } = useI18n();
  const socials = Object.entries(page.social_links ?? {}).filter(([, url]) => typeof url === 'string' && url);
  if (!hasContact(page)) return null;
  return (
    <ul className="contact-list" aria-label={t('page.contact_details')}>
      {page.contact_phone && (
        <li><PhoneIcon /><a href={`tel:${page.contact_phone.replace(/[^\d+]/g, '')}`}>{page.contact_phone}</a></li>
      )}
      {page.contact_email && (
        <li><MailIcon /><a href={`mailto:${page.contact_email}`}>{page.contact_email}</a></li>
      )}
      {page.website_url && (
        <li><GlobeIcon /><a href={ensureHttp(page.website_url)} rel="noopener" target="_blank">{hostOf(page.website_url)}</a></li>
      )}
      {page.address && (
        <li><PinIcon /><a href={`https://maps.google.com/?q=${encodeURIComponent(page.address)}`} rel="noopener" target="_blank">{page.address}</a></li>
      )}
      {socials.map(([network, url]) => (
        <li key={network}>
          <SocialIcon network={network} />
          <a href={ensureHttp(url)} rel="noopener" target="_blank">{labelFor(network)}</a>
        </li>
      ))}
    </ul>
  );
}

function labelFor(network: string): string {
  const known: Record<string, string> = { instagram: 'Instagram', facebook: 'Facebook', x: 'X', twitter: 'X (Twitter)', tiktok: 'TikTok', linkedin: 'LinkedIn', youtube: 'YouTube', yelp: 'Yelp', whatsapp: 'WhatsApp' };
  return known[network.toLowerCase()] ?? network.charAt(0).toUpperCase() + network.slice(1);
}

interface HeaderProps {
  name: string;
  logoUrl?: string | null;
  coverUrl?: string | null;
  /** An illustration sits whole on its background; a photograph fills the band. */
  coverFit?: 'cover' | 'contain';
  headline?: string | null;
  description?: string | null;
  children?: React.ReactNode;
}

export function BusinessHeader({ name, logoUrl, coverUrl, coverFit = 'cover', headline, description, children }: HeaderProps) {
  return (
    <header className={`biz-header${coverUrl ? ' has-cover' : ''}`}>
      {coverUrl && <div className={`biz-cover${coverFit === 'contain' ? ' biz-cover--contain' : ''}`}><img src={coverUrl} alt="" loading="eager" fetchPriority="high" /></div>}
      <div className="biz-identity">
        {logoUrl ? (
          <img className="biz-logo" src={logoUrl} alt={`${name} logo`} width={72} height={72} />
        ) : (
          <div className={`biz-logo biz-logo--initial${businessInitials(name).length > 1 ? ' biz-logo--pair' : ''}`} aria-hidden="true">{businessInitials(name) || '?'}</div>
        )}
        <div className="biz-title">
          <h1>{name}</h1>
          {headline && <p className="biz-headline">{headline}</p>}
        </div>
        {/*
          Beside the name rather than in the footer: somebody who cannot
          read the page should not have to scroll the whole thing to find
          the control that fixes that. There is exactly one per page —
          `PageFooter` shows it only on screens with no business header —
          because two would raise the question of which one is in charge.
        */}
        <LanguageSwitcher className="biz-language" />
      </div>
      {description && <p className="biz-description">{description}</p>}
      {children}
    </header>
  );
}

export function Policies({ page }: { page: Pick<PublicPage, 'booking_policy' | 'cancellation_policy'> & PaymentFields }) {
  const { t } = useI18n();
  const items: [string, ReactNode][] = [];
  // The HEADINGS are the product's and are translated; the policies
  // themselves are the business's own words and are shown as written.
  if (page.booking_policy) items.push([t('policies.booking'), <p>{page.booking_policy}</p>]);
  if (hasPaymentInfo(page)) items.push([t('policies.how_to_pay'), <PaymentMethods page={page} />]);
  if (page.cancellation_policy) items.push([t('policies.cancellation'), <p>{page.cancellation_policy}</p>]);
  if (items.length === 0) return null;
  return (
    <section className="policies" aria-labelledby="policies-h">
      <h2 id="policies-h">{t('policies.heading')}</h2>
      {items.map(([title, body]) => (
        <details key={title} className="policy">
          <summary>{title}</summary>
          <div className="policy-body">{body}</div>
        </details>
      ))}
    </section>
  );
}

/**
 * The page's own controls: how it looks, and what language it is read
 * in. Both are the visitor's to change and neither touches the
 * business's settings.
 */
/**
 * `language` is for screens with no business header of their own — the
 * customer's account, sign-in and invitation pages. The booking and
 * manage pages carry the control in their header instead.
 */
export function PageFooter({ poweredBy = true, language = true }: { poweredBy?: boolean; language?: boolean }) {
  const { t } = useI18n();
  return (
    <footer className="page-footer">
      <Appearance placement="top" showLabel labels={{
        appearance: t('appearance.control'),
        theme: t('appearance.theme'),
        light: t('appearance.light'),
        dark: t('appearance.dark'),
        reduceMotion: t('appearance.reduce_motion'),
        useDevice: t('appearance.use_device'),
        followingDevice: t('appearance.following_device'),
      }} />
      {language && <LanguageSwitcher />}
      {poweredBy ? <a href="/">{t('page.powered_by')}</a> : null}
      {/* The published policies (0046), on every public screen. */}
      <LegalFooter compact label={t('legal.policies')} />
    </footer>
  );
}

